import { useEffect, useRef, useState } from "react";
import {
  Button,
  DraggableCarousel,
  SnapCarousel,
  Card,
  CardBody,
} from "../../../components/ui";
import depositv1Service from "../services/deposit.service";
import { DeviceType } from "../../../models/constants/deviceType";
import localization from "../../../config/localization";
import {
  AvailableDepositBonusesResponse,
  DepositPaymentMethodListResponseList,
  DepositTypeListResponseList,
  TopRecommendedTransferMethodsResponse,
  UserMobileNumberDetailResponse,
  UserRecentUsedDepositTypesResponse,
  UserDepositSummaryResponse,
} from "../models/response";
import { DepositTypeListRequest } from "../models/request";
import { useNavigate } from "react-router-dom";
import { localStorageService } from "../../../services";
import { BlobContainerConsts } from "../../../models/constants/blobContainerConsts";
import { BLOB_IMAGE_URL } from "../../../config/appConfig";
import { DepositPaymentMethod } from "../models/constants";
import CreateDepositRequest from "./CreateDepositRequest";
import { useAppDispatch } from "../../../store/hooks";
import { changeLoaderState } from "../../../reducers/appStateReducer";
import userDeviceStore from "../../../store/device.store";
import { Modal } from "../../../components/ui/modals";

interface DepositV2Props {
  depositFlow: string;
  deviceType?: (typeof DeviceType)[keyof typeof DeviceType];
}

export default function DepositV2({ depositFlow, deviceType }: DepositV2Props) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [showPendingDepositWarningModal, setShowPendingDepositWarningModal] =
    useState<boolean>(false);
  const [topRecommendedTransferMethods, setTopRecommendedTransferMethods] =
    useState<TopRecommendedTransferMethodsResponse | null>(null);
  const [userRecentUsedDepositTypes, setUserRecentUsedDepositTypes] =
    useState<UserRecentUsedDepositTypesResponse | null>(null);
  const [depositTypeWithAbcBankDetails, setDepositTypeWithAbcBankDetails] =
    useState<DepositTypeListResponseList[] | null>(null);
  const [depositPaymentMethod, setDepositPaymentMethod] = useState<
    DepositPaymentMethodListResponseList[] | null
  >(null);
  const [userMobileDetail, setUserMobileDetail] =
    useState<UserMobileNumberDetailResponse | null>(null);
  const [selectedDepositPaymentMethod, setSelectedDepositPaymentMethod] =
    useState<DepositPaymentMethodListResponseList | null>(null);
  const [showVerifyMobileModal, setShowVerifyMobileModal] =
    useState<boolean>(false);
  const [depositTypeListForMethod, setDepositTypeListForMethod] = useState<
    DepositTypeListResponseList[] | null
  >(null);
  const [selectedDepositType, setSelectedDepositType] =
    useState<DepositTypeListResponseList | null>(null);
  const [availableDepositBonuses, setAvailableDepositBonuses] =
    useState<AvailableDepositBonusesResponse | null>(null);
  const [userDepositSummary, setUserDepositSummary] =
    useState<UserDepositSummaryResponse | null>(null);
  const usernameRef = useRef<string | null>("User");

  const [itemWidth, setItemWidth] = useState("100%");
  const [isMobile, setIsMobile] = useState(false);
  const accessModeSource = userDeviceStore((state) => state.accessModeSource);

  useEffect(() => {
    dispatch(changeLoaderState(true));
  }, []);

  useEffect(() => {
    const update = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
      if (width >= 1024) {
        setItemWidth("20vw");
      } else if (width >= 640) {
        setItemWidth("70vw");
      } else {
        setItemWidth("82vw");
      }
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    if (
      depositPaymentMethod?.length &&
      depositTypeWithAbcBankDetails?.length &&
      !selectedDepositPaymentMethod
    ) {
      handlePaymentMethodClick(depositPaymentMethod[0]);
    }
  }, [depositPaymentMethod, depositTypeWithAbcBankDetails]);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    try {
      const { pendingOnlineDeposits, pendingOfflineDeposits } =
        await depositv1Service.getUserPendingDeposits();
      if (pendingOnlineDeposits >= 1 && pendingOfflineDeposits >= 1) {
        setShowPendingDepositWarningModal(true);
        return;
      }
      const topRecommendedTransferMethods =
        await depositv1Service.getTopRecommendedTransferMethods();
      if (topRecommendedTransferMethods) {
        setTopRecommendedTransferMethods(topRecommendedTransferMethods);
      }
      const userRecentUsedDepositTypes =
        await depositv1Service.getUserRecentUsedDepositTypes();
      if (userRecentUsedDepositTypes) {
        setUserRecentUsedDepositTypes(userRecentUsedDepositTypes);
      }
      const summary = await depositv1Service.getUserDepositSummary();
      if (summary) {
        setUserDepositSummary(summary);
      }
      if (summary && topRecommendedTransferMethods) {
        const request: DepositTypeListRequest = {
          isActive: true,
          age: summary.daysOld,
          transactionAmount: summary.depositAmount ?? 0,
          transactionsCount: summary.depositCount ?? 0,
        };
        const depositTypeListResponse =
          await depositv1Service.getDepositTypeWithAbcBankDetails(
            request,
            summary,
            topRecommendedTransferMethods,
          );
        if (
          (pendingOnlineDeposits >= 1 || pendingOfflineDeposits >= 1) &&
          (!depositTypeListResponse || depositTypeListResponse.length === 0)
        ) {
          setShowPendingDepositWarningModal(true);
          return;
        }
        if (depositTypeListResponse) {
          setDepositTypeWithAbcBankDetails(depositTypeListResponse);
          const depositPaymentMethod =
            await depositv1Service.getDepositPaymentMethod(
              depositTypeListResponse ?? [],
              userRecentUsedDepositTypes,
              topRecommendedTransferMethods,
            );
          if (depositPaymentMethod) {
            setDepositPaymentMethod(depositPaymentMethod);
          }
        }
      }
      const response = await depositv1Service.getUserMobileDetail();
      setUserMobileDetail(response);
      if (!userMobileDetail?.ismobileverified) {
        usernameRef.current = localStorageService.getItem<string>("username");
      }
    } finally {
      dispatch(changeLoaderState(false));
    }
  };

  const handlePendingDepositClose = () => {
    navigate("/deposithistory");
  };

  const handleVerifyProceed = () => {
    navigate("/profile");
  };

  const handlePaymentMethodClick = async (
    method: DepositPaymentMethodListResponseList,
  ) => {
    setSelectedDepositPaymentMethod(method);

    if (
      method.name?.toLowerCase() === DepositPaymentMethod.Crypto &&
      !userMobileDetail?.ismobileverified
    ) {
      setShowVerifyMobileModal(true);
      return;
    }

    const depositTypeListResponse =
      await depositv1Service.getDepositTypesForMethod(
        method,
        depositTypeWithAbcBankDetails ?? [],
        userRecentUsedDepositTypes ?? null,
      );
    if (depositTypeListResponse) {
      const sortedList = [...depositTypeListResponse].sort((a, b) => {
        if (a.sequence === 0 && b.sequence !== 0) return 1;
        if (a.sequence !== 0 && b.sequence === 0) return -1;

        if (a.sequence !== b.sequence) {
          return a.sequence - b.sequence;
        }

        const getRow = (x: DepositTypeListResponseList) => {
          if (!topRecommendedTransferMethods?.details) {
            return Number.MAX_SAFE_INTEGER;
          }

          const match = topRecommendedTransferMethods.details.find((r) => {
            const upiMatch =
              (!method.ismanualcreation &&
                r?.upiMethodName?.toLowerCase() ===
                method.name?.toLowerCase()) ||
              method.ismanualcreation;

            if (!upiMatch) return false;

            const abcBankMatch =
              x?.allowedABCBankTransfers &&
              r?.bankName &&
              x.allowedABCBankTransfers.bankName?.toLowerCase() ===
              r.bankName.toLowerCase() &&
              x.id === r.depositTypeId;

            const normalMatch =
              !r?.bankName &&
              x?.name?.toLowerCase() === r?.name?.toLowerCase() &&
              x.id === r.depositTypeId;

            return abcBankMatch || normalMatch;
          });

          return match?.rowNumber ?? Number.MAX_SAFE_INTEGER;
        };

        return getRow(a) - getRow(b);
      });

      setDepositTypeListForMethod(sortedList);
    }
  };

  const onDepositTypeClick = async (item: DepositTypeListResponseList) => {
    setSelectedDepositType(item);
    const depositbonuses = await depositv1Service.getAvailableDepositBonuses(
      accessModeSource.sourceType,
    );
    setAvailableDepositBonuses(depositbonuses);
  };

  if (selectedDepositType) {
    const child = (
      <CreateDepositRequest
        selectedDepositType={selectedDepositType}
        availableDepositBonuses={availableDepositBonuses}
        userMobileDetail={userMobileDetail}
        onBack={() => setSelectedDepositType(null)}
        depositFlow={depositFlow}
        userDepositSummary={userDepositSummary}
        selectedDepositPaymentMethod={selectedDepositPaymentMethod}
        userRecentUsedDepositTypes={userRecentUsedDepositTypes}
        topRecommendedTransferMethods={topRecommendedTransferMethods}
        deviceType={deviceType}
      />
    );

    return deviceType !== DeviceType.Web ? (
      child
    ) : (
      <Card>
        <CardBody showGradient>
          <div className="w-full max-w-4xl mx-auto">{child}</div>
        </CardBody>
      </Card>
    );
  }

  const content = (
    <div className="px-4">
      <div className="w-full bg-black">
        <DraggableCarousel
          className="w-full max-w-[95%] mx-auto"
          gap="gap-3"
          activeIndex={depositPaymentMethod?.findIndex(
            (m) => m.id === selectedDepositPaymentMethod?.id,
          )}
        >
          {depositPaymentMethod?.map((method) => {
            const isActive = method.id === selectedDepositPaymentMethod?.id;

            return (
              <Button
                key={method.id}
                onClick={() => handlePaymentMethodClick(method)}
                className="relative flex items-center justify-center flex-shrink-0 rounded-t-lg py-1 transition-all"
              >
                <img
                  src={`${BLOB_IMAGE_URL}${BlobContainerConsts.DepositPaymentMethod}/${method.imageurl}`}
                  alt={method.name}
                  className="h-12 w-full object-contain"
                />

                {isActive && (
                  <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#FF94B1]" />
                )}
              </Button>
            );
          })}
        </DraggableCarousel>
      </div>
    </div>
  );
  const cardcontent = (
    <div className="px-4">
      <div className="mt-10">
        <SnapCarousel
          key={(depositTypeListForMethod ?? []).map((d) => d.id).join("-")}
          infinite={true}
          startIndex={0}
          dotsVariant="centeredCounter"
          showAdjacent={true}
          gap={isMobile ? "gap-0" : "gap-2"}
          itemWidth={itemWidth}
        >
          {depositTypeListForMethod?.map((depositType) => (
            <div key={depositType.id}>
              {isMobile ? (
                <div className="relative mx-auto h-[333px] w-full max-w-[255px] rounded-[14px] border-2 border-[#2C2C2C] bg-black-gradient px-4 pb-[22px] pt-8 text-white">
                  {depositType.isRecommenededTransferMethod && (
                    <div className="absolute top-[-1px]">
                      <img
                        src={"/images/img-recommended.svg"}
                        alt="Recommend"
                        className="h-full w-56"
                      />
                    </div>
                  )}

                  <div className="flex h-full flex-col items-center">
                    <div className="mt-2 flex h-[88px] w-[88px] items-center justify-center rounded-2xl bg-white">
                      <img
                        src={
                          depositType.allowedABCBankTransfers?.logoUrl
                            ? depositType.allowedABCBankTransfers.logoUrl
                            : `${BLOB_IMAGE_URL}${BlobContainerConsts.Banks}/${depositType.logoUrl}`
                        }
                        alt={depositType.name}
                        className="h-14 w-14 object-contain"
                      />
                    </div>

                    <p className="mt-4 text-center text-[14px] font-semibold leading-6">
                      {depositType.allowedABCBankTransfers?.bankName ??
                        depositType.accountName}
                    </p>

                    <div className="mt-4 flex justify-center">
                      <div className="flex items-center gap-1.5 rounded-full border border-[#3B3B3B] bg-black px-5 py-2.5">
                        <span className="text-[14px] font-normal text-[#C7C7CC]">
                          {localization.min.replace(" ₹", "")}
                        </span>

                        <span className="text-[15px] font-medium text-white">
                          {"\u20B9"}
                          {depositType.minimumAmount}
                        </span>

                        <span className="text-[14px] font-normal text-[#C7C7CC]">
                          -
                        </span>

                        <span className="text-[14px] font-normal text-[#C7C7CC]">
                          {localization.max.replace(" ₹", "")}
                        </span>

                        <span className="text-[15px] font-medium text-white">
                          {"\u20B9"}
                          {depositType.maximumAmount}
                        </span>
                      </div>
                    </div>

                    <div className="mt-auto w-full">
                      <Button
                        onClick={() => onDepositTypeClick(depositType)}
                        className="w-full rounded-md bg-secondary-gradient py-[10px] text-sm font-semibold"
                      >
                        {localization.select}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative w-full max-w-64 mx-auto rounded-2xl bg-black-gradient border-2 border-[#2C2C2C] px-4 py-5 text-white">
                  {depositType.isRecommenededTransferMethod && (
                    <div className="absolute top-[-1px]">
                      <img
                        src={"/images/img-recommended.svg"}
                        alt="Recommend"
                        className="h-full w-56"
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-center mt-6">
                    <img
                      src={
                        depositType.allowedABCBankTransfers?.logoUrl
                          ? depositType.allowedABCBankTransfers.logoUrl
                          : `${BLOB_IMAGE_URL}${BlobContainerConsts.Banks}/${depositType.logoUrl}`
                      }
                      alt={depositType.name}
                      className="object-contain h-24 w-24"
                    />
                  </div>

                  <p className="mt-2 text-center font-bold text-sm leading-7">
                    {depositType.allowedABCBankTransfers?.bankName ??
                      depositType.accountName}
                  </p>

                  <div className="flex justify-center mt-5">
                    <div className="px-4 py-2 rounded-full border border-[#3B3B3B] bg-black flex items-center gap-1">
                      <span className="text-[#C7C7CC] text-[13px] font-normal">
                        {localization.min.replace(" ₹", "")}
                      </span>

                      <span className="text-white text-[14px] font-medium">
                        {"\u20B9"}
                        {depositType.minimumAmount}
                      </span>

                      <span className="text-[#C7C7CC] text-[13px] font-normal">
                        -
                      </span>

                      <span className="text-[#C7C7CC] text-[13px] font-normal">
                        {localization.max.replace(" ₹", "")}
                      </span>

                      <span className="text-white text-[14px] font-medium">
                        {"\u20B9"}
                        {depositType.maximumAmount}
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={() => onDepositTypeClick(depositType)}
                    className="mt-5 w-full rounded-md bg-secondary-gradient py-[10px] text-sm font-semibold"
                  >
                    {localization.select}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </SnapCarousel>
      </div>

      {showPendingDepositWarningModal && (
        <Modal isOpen={showPendingDepositWarningModal} showImg={false} onClose={handlePendingDepositClose}>
          <p className="mt-2 px-4 text-[18px] font-semibold leading-[28px] text-gray-600">
            {localization.depositprocessingpara1}
          </p>

          <Button
            onClick={handlePendingDepositClose}
            className="mt-6 w-full rounded-lg bg-[#111111] py-3.5 font-semibold text-white hover:bg-[#EDEAEA] hover:text-black transition-all duration-200"
          >
            {localization.ok_btn}
          </Button>
        </Modal>
      )}

      {showVerifyMobileModal && (
        <Modal isOpen={showVerifyMobileModal} onClose={handleVerifyProceed}>
          <p className="mt-3 text-[18px] leading-7 text-gray-600">
            {localization.hi_lbl}{" "}
            <span className="font-bold text-black">{usernameRef.current}</span>
          </p>

          <p className="mt-2 px-4 text-[18px] font-semibold leading-7 text-gray-600">
            {localization.crypto_deposit_mobile_verify}
          </p>

          <Button
            onClick={handleVerifyProceed}
            className="mt-6 w-full rounded-lg bg-[#111111] py-3.5 font-semibold text-white hover:bg-[#EDEAEA] hover:text-black transition-all duration-200"
          >
            {localization.proceed.toUpperCase()}
          </Button>
        </Modal>
      )}
    </div>
  );

  const header = deviceType === DeviceType.Web && (
    <div className="text-center text-white text-3xl font-bold mb-4">
      {localization.choose_your_deposit_method}
    </div>
  );

  return deviceType !== DeviceType.Web ? (
    <>
      {content}
      {cardcontent}
    </>
  ) : (
    <>
      {content}
      <Card>
        <CardBody showGradient>
          <div className="w-full mx-auto">
            {header}
            {cardcontent}
          </div>
        </CardBody>
      </Card>
    </>
  );
}
